// CultureText.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Form, Button, Modal, Badge, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { 
  getAllCultureTexts, 
  addCultureText, 
  updateCultureText, 
  deleteCultureText,
  getAllCultures
} from '../services/cultureTextService';

const CultureText = () => {
  // State tanımlamaları
  const [cultureTexts, setCultureTexts] = useState([]);
  const [cultures, setCultures] = useState([]);
  const [filteredTexts, setFilteredTexts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Filtreleme
  const [selectedCultureId, setSelectedCultureId] = useState('');
  const [searchKey, setSearchKey] = useState('');
  const [searchValue, setSearchValue] = useState('');

  // Yeni ekleme
  const [showAddModal, setShowAddModal] = useState(false);
  const [newText, setNewText] = useState({
    key: '',
    value: '',
    cultureId: ''
  });

  // Düzenleme
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState({
    id: '',
    key: '',
    value: '',
    cultureId: ''
  });

  // Silme
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  // Veri yükleme fonksiyonları
  const fetchCultureTexts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllCultureTexts();
      if (response.Status) {
        setCultureTexts(response.Data.Items || []);
        setFilteredTexts(response.Data.Items || []);
      } else {
        toast.error(response.Message || 'Kelimeler yüklenemedi.');
      }
    } catch (error) {
      console.error('Culture texts yükleme hatası:', error);
      toast.error('Kelimeler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCultures = useCallback(async () => {
    try {
      const cultures = await getAllCultures();
      setCultures(cultures || []);
    } catch (error) {
      console.error('Cultures yükleme hatası:', error);
      toast.error('Diller yüklenirken hata oluştu.');
    }
  }, []);

  useEffect(() => {
    fetchCultureTexts();
    fetchCultures();
  }, [fetchCultureTexts, fetchCultures]);

  // Filtreleme fonksiyonu
  useEffect(() => {
    let filtered = cultureTexts;

    if (selectedCultureId) {
      filtered = filtered.filter(text => text.cultureId === selectedCultureId);
    }

    if (searchKey) {
      filtered = filtered.filter(text => 
        text.key.toLowerCase().includes(searchKey.toLowerCase())
      );
    }

    if (searchValue) {
      filtered = filtered.filter(text => 
        text.value.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    setFilteredTexts(filtered);
  }, [cultureTexts, selectedCultureId, searchKey, searchValue]);

  // Ekleme fonksiyonu
  const handleAdd = async () => {
    if (!newText.key.trim() || !newText.value.trim() || !newText.cultureId) {
      toast.warning('Lütfen tüm alanları doldurun.');
      return;
    }

    try {
      setProcessing(true);
      const response = await addCultureText(newText);
      
      if (response.Status) {
        toast.success(response.Message || 'Kelime başarıyla eklendi.');
        setShowAddModal(false);
        setNewText({ key: '', value: '', cultureId: '' });
        await fetchCultureTexts();
      } else {
        toast.error(response.Message || 'Kelime eklenirken hata oluştu.');
      }
    } catch (error) {
      console.error('Ekleme hatası:', error);
      toast.error('Kelime eklenirken hata oluştu.');
    } finally {
      setProcessing(false);
    }
  };

  // Güncelleme fonksiyonu
  const handleUpdate = async () => {
    if (!editText.key.trim() || !editText.value.trim()) {
      toast.warning('Anahtar ve değer alanları boş olamaz.');
      return;
    }

    try {
      setProcessing(true);
      const response = await updateCultureText(editText);
      
      if (response.Status) {
        toast.success(response.Message || 'Kelime başarıyla güncellendi.');
        setEditingId(null);
        setEditText({ id: '', key: '', value: '', cultureId: '' });
        await fetchCultureTexts();
      } else {
        toast.error(response.Message || 'Kelime güncellenirken hata oluştu.');
      }
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast.error('Kelime güncellenirken hata oluştu.');
    } finally {
      setProcessing(false);
    }
  };

  // Silme fonksiyonu
  const handleDelete = async () => {
    if (!deleteCandidate) return;

    try {
      setProcessing(true);
      const response = await deleteCultureText(deleteCandidate.id);
      
      if (response.Status) {
        toast.success(response.Message || 'Kelime başarıyla silindi.');
        setShowDeleteModal(false);
        setDeleteCandidate(null);
        await fetchCultureTexts();
      } else {
        toast.error(response.Message || 'Kelime silinirken hata oluştu.');
      }
    } catch (error) {
      console.error('Silme hatası:', error);
      toast.error('Kelime silinirken hata oluştu.');
    } finally {
      setProcessing(false);
    }
  };

  // Yardımcı fonksiyonlar
  const getCultureName = (cultureId) => {
    const culture = cultures.find(c => c.id === cultureId);
    return culture ? culture.name : 'Bilinmeyen';
  };

  const handleStartEdit = (text) => {
    setEditingId(text.id);
    setEditText({
      id: text.id,
      key: text.key,
      value: text.value,
      cultureId: text.cultureId
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText({ id: '', key: '', value: '', cultureId: '' });
  };

  const resetFilters = () => {
    setSelectedCultureId('');
    setSearchKey('');
    setSearchValue('');
  };

  return (
    <div className="container-fluid p-4">
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Kelime Yönetimi</h4>
            <Button 
              variant="primary" 
              onClick={() => setShowAddModal(true)}
              disabled={processing}
            >
              + Yeni Kelime Ekle
            </Button>
          </div>
        </Card.Header>
        
        <Card.Body>
          {/* Filtreleme Alanı */}
          <Row className="mb-4">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Dil Filtresi</Form.Label>
                <Form.Select
                  value={selectedCultureId}
                  onChange={(e) => setSelectedCultureId(e.target.value)}
                >
                  <option value="">Tüm Diller</option>
                  {cultures.map(culture => (
                    <option key={culture.id} value={culture.id}>
                      {culture.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Anahtar Ara</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Anahtar kelime..."
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Değer Ara</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Değer ara..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>&nbsp;</Form.Label>
                <div className="d-grid">
                  <Button variant="outline-secondary" onClick={resetFilters}>
                    Filtreleri Temizle
                  </Button>
                </div>
              </Form.Group>
            </Col>
          </Row>

          {/* İstatistikler */}
          <Row className="mb-3">
            <Col>
              <Alert variant="info" className="mb-0">
                <strong>Toplam:</strong> {cultureTexts.length} kelime | 
                <strong> Filtrelenmiş:</strong> {filteredTexts.length} kelime
              </Alert>
            </Col>
          </Row>

          {/* Tablo */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </Spinner>
            </div>
          ) : (
            <Table bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '25%' }}>Anahtar</th>
                  <th style={{ width: '35%' }}>Değer</th>
                  <th style={{ width: '15%' }}>Dil</th>
                  <th style={{ width: '10%' }}>Durum</th>
                  <th style={{ width: '15%' }}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredTexts.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      Kelime bulunamadı.
                    </td>
                  </tr>
                ) : (
                  filteredTexts.map((text) => (
                    <tr key={text.id}>
                      <td>
                        {editingId === text.id ? (
                          <Form.Control
                            size="sm"
                            value={editText.key}
                            onChange={(e) => setEditText({...editText, key: e.target.value})}
                          />
                        ) : (
                          <code>{text.key}</code>
                        )}
                      </td>
                      <td>
                        {editingId === text.id ? (
                          <Form.Control
                            size="sm"
                            value={editText.value}
                            onChange={(e) => setEditText({...editText, value: e.target.value})}
                          />
                        ) : (
                          text.value
                        )}
                      </td>
                      <td>
                        <Badge bg="secondary">
                          {getCultureName(text.cultureId)}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={text.status ? "success" : "danger"}>
                          {text.status ? "Aktif" : "Pasif"}
                        </Badge>
                      </td>
                      <td>
                        {editingId === text.id ? (
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant="success"
                              onClick={handleUpdate}
                              disabled={processing}
                            >
                              {processing ? <Spinner size="sm" /> : 'Kaydet'}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={handleCancelEdit}
                              disabled={processing}
                            >
                              İptal
                            </Button>
                          </div>
                        ) : (
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant="warning"
                              onClick={() => handleStartEdit(text)}
                              disabled={editingId !== null || processing}
                            >
                              Düzenle
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => {
                                setDeleteCandidate(text);
                                setShowDeleteModal(true);
                              }}
                              disabled={editingId !== null || processing}
                            >
                              Sil
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Ekleme Modalı */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Yeni Kelime Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Anahtar *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Örn: welcome_message"
                    value={newText.key}
                    onChange={(e) => setNewText({...newText, key: e.target.value})}
                  />
                  <Form.Text className="text-muted">
                    Kod içinde kullanılacak anahtar değer
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Dil *</Form.Label>
                  <Form.Select
                    value={newText.cultureId}
                    onChange={(e) => setNewText({...newText, cultureId: e.target.value})}
                  >
                    <option value="">Dil Seçin</option>
                    {cultures.map(culture => (
                      <option key={culture.id} value={culture.id}>
                        {culture.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Değer *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Kullanıcıya gösterilecek metin"
                value={newText.value}
                onChange={(e) => setNewText({...newText, value: e.target.value})}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            İptal
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAdd}
            disabled={processing}
          >
            {processing ? <Spinner size="sm" className="me-2" /> : ''}
            Ekle
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Silme Modalı */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Kelime Sil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>"{deleteCandidate?.key}"</strong> anahtarını silmek istediğinizden emin misiniz?</p>
          <p className="text-muted">Değer: {deleteCandidate?.value}</p>
          <Alert variant="warning">
            <small>Bu işlem geri alınamaz ve kelime pasif duruma geçer.</small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            İptal
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            disabled={processing}
          >
            {processing ? <Spinner size="sm" className="me-2" /> : ''}
            Sil
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CultureText;